import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import CloseIcon from '@mui/icons-material/Close';
import { config } from '../config';

const LargeModalOverlay = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.8);
  display: ${({ isOpen }) => isOpen ? 'flex' : 'none'};
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const LargeModalContent = styled.div`
  width: 95%;
  height: 95%;
  background: white;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  position: relative;
  padding: 0;
`;

const ModalHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const LargeModalTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  color: #333;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #666;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalBody = styled.div`
  flex: 1;
  overflow: hidden;
  position: relative;
`;

const FullscreenPdfViewer = styled.object`
  width: 100%;
  height: 100%;
  border: none;
`;

const TabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid #e0e0e0;
  margin-bottom: 16px;
`;

const Tab = styled.button<{ active: boolean }>`
  padding: 12px 24px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 16px;
  font-weight: ${({ active }) => (active ? '600' : '400')};
  color: ${({ active }) => (active ? '#333' : '#666')};
  border-bottom: 2px solid ${({ active }) => (active ? '#95d0dd' : 'transparent')};
  transition: all 0.2s ease;

  &:hover {
    color: #333;
  }
`;

const PdfContainer = styled.div<{ visible: boolean }>`
  display: ${({ visible }) => (visible ? 'block' : 'none')};
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
`;

interface TermsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const TermsModal = ({ isOpen, onClose }: TermsModalProps) => {
  const [activeTab, setActiveTab] = useState<'terms' | 'privacy'>('terms');

  // Resetear la pestaña activa cuando se cierra el modal
  useEffect(() => {
    if (!isOpen) {
      setActiveTab('terms');
    }
  }, [isOpen]);

  return (
    <LargeModalOverlay isOpen={isOpen}>
      <LargeModalContent>
        <ModalHeader>
          <LargeModalTitle>
            {activeTab === 'terms' ? 'Términos y Condiciones Generales' : 'Política de Privacidad'}
          </LargeModalTitle>
          <CloseButton onClick={onClose}>
            <CloseIcon />
          </CloseButton>
        </ModalHeader>
        <TabContainer>
          <Tab 
            active={activeTab === 'terms'} 
            onClick={() => setActiveTab('terms')}
          >
            Términos y Condiciones
          </Tab>
          <Tab 
            active={activeTab === 'privacy'} 
            onClick={() => setActiveTab('privacy')}
          >
            Política de Privacidad
          </Tab>
        </TabContainer>
        <ModalBody>
          <PdfContainer visible={activeTab === 'terms'}>
            <FullscreenPdfViewer
              data={config.business.termsUrl}
              type="application/pdf"
              title="Términos y Condiciones Generales"
            >
              <p>Tu navegador no puede mostrar el PDF directamente. 
                <a 
                  href={config.business.termsUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  Haz clic aquí para descargarlo
                </a>
              </p>
            </FullscreenPdfViewer>
          </PdfContainer>
          <PdfContainer visible={activeTab === 'privacy'}>
            <FullscreenPdfViewer
              data={config.business.privacyUrl}
              type="application/pdf"
              title="Política de Privacidad"
            >
              <p>Tu navegador no puede mostrar el PDF directamente. 
                <a 
                  href={config.business.privacyUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  Haz clic aquí para descargarlo
                </a>
              </p>
            </FullscreenPdfViewer>
          </PdfContainer>
        </ModalBody>
      </LargeModalContent>
    </LargeModalOverlay>
  );
};

export default TermsModal;
